// PRD.275 — Elia AI Assistant Drawer (Phase 1, UI only).
//
// Responsive container that adapts by viewport (FR-2..FR-4):
//   docked  — fixed floating panel on wide desktop, page stays usable, no scrim
//   overlay — right-side drawer + dimmed backdrop on narrow desktop / tablet
//   mobile  — full-screen conversation view
// Portals to <body>, provides dialog semantics, Escape-to-close, focus return,
// a focus trap for the modal variants, and background scroll lock.

import { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { ELIA_COLORS, ELIA_DRAWER_WIDTH, ELIA_FONT, ELIA_Z_INDEX } from './elia-theme';
import { useEliaAi } from './EliaAiProvider';
import { useEliaLayoutMode, usePrefersReducedMotion } from './useEliaLayout';
import { EliaAiHeader } from './EliaAiHeader';
import { EliaContextBanner } from './EliaContextBanner';
import { EliaConversation } from './EliaConversation';
import { EliaComposer } from './EliaComposer';

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

export function EliaAiDrawer() {
  const { isOpen, close, messages, isBusy, context, sendMessage, retry, reset, onChangeContext } =
    useEliaAi();
  const mode = useEliaLayoutMode();
  const reducedMotion = usePrefersReducedMotion();
  const panelRef = useRef<HTMLDivElement>(null);

  const isModal = mode !== 'docked';

  // Escape closes the overlay/full-screen variants (FR-1).
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') close();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [isOpen, close]);

  // Lock background scroll while a modal variant is open (FR-3).
  useEffect(() => {
    if (!isOpen || !isModal) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen, isModal]);

  // Move focus into the drawer on open.
  useEffect(() => {
    if (!isOpen) return;
    const panel = panelRef.current;
    if (!panel) return;
    const first = panel.querySelector<HTMLElement>(FOCUSABLE);
    first?.focus();
  }, [isOpen]);

  // Simple focus trap for the modal variants (FR-3 "keep focus inside").
  const onPanelKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (!isModal || e.key !== 'Tab') return;
    const panel = panelRef.current;
    if (!panel) return;
    const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
      (el) => el.offsetParent !== null,
    );
    if (focusable.length === 0) return;
    const firstEl = focusable[0];
    const lastEl = focusable[focusable.length - 1];
    const active = document.activeElement as HTMLElement | null;
    if (e.shiftKey && active === firstEl) {
      e.preventDefault();
      lastEl.focus();
    } else if (!e.shiftKey && active === lastEl) {
      e.preventDefault();
      firstEl.focus();
    }
  };

  if (!isOpen) return null;

  const firstName = context.patientName?.split(' ')[0];
  const placeholder = firstName ? `Ask about ${firstName}'s current visit...` : 'Ask Elia AI...';

  const transition = reducedMotion ? undefined : 'transform 250ms ease-out';

  const panelPositionStyle: React.CSSProperties =
    mode === 'mobile'
      ? { position: 'fixed', inset: 0, borderRadius: 0 }
      : mode === 'overlay'
        ? {
            position: 'fixed',
            top: 0,
            right: 0,
            bottom: 0,
            width: `min(${ELIA_DRAWER_WIDTH}px, 92vw)`,
            borderRadius: 0,
            borderLeft: `1px solid ${ELIA_COLORS.border}`,
          }
        : {
            // docked — floating card below the top bar, page remains usable
            position: 'fixed',
            top: 'calc(var(--topbar-height, 56px) + 8px)',
            right: 8,
            bottom: 8,
            width: ELIA_DRAWER_WIDTH,
            borderRadius: 16,
            border: `1px solid ${ELIA_COLORS.border}`,
          };

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={isModal}
      aria-label="Elia AI"
      onKeyDown={onPanelKeyDown}
      style={{
        ...panelPositionStyle,
        zIndex: ELIA_Z_INDEX,
        background: ELIA_COLORS.surface,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1), 0px 4px 6px -4px rgba(0,0,0,0.1)',
        fontFamily: ELIA_FONT,
        transform: 'translateX(0)',
        transition,
      }}
    >
      <EliaAiHeader showBack={mode === 'mobile'} onBack={close} onClose={close} onReset={reset} />
      <EliaContextBanner
        patientName={context.patientName}
        contextLabel={context.contextLabel}
        onChange={onChangeContext}
      />
      <EliaConversation
        messages={messages}
        busy={isBusy}
        onSelectSuggestion={sendMessage}
        onRetry={retry}
      />
      <EliaComposer onSend={sendMessage} busy={isBusy} placeholder={placeholder} />
    </div>
  );

  return createPortal(
    <>
      {isModal ? (
        <div
          aria-hidden="true"
          onClick={close}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: ELIA_Z_INDEX - 1,
            background: 'rgba(0,0,0,0.35)',
          }}
        />
      ) : null}
      {panel}
    </>,
    document.body,
  );
}
